/**
 * Constantes partagées pour la Diffusion (LOT 3 — iter92, unification iter95).
 * SUPPORTS = superset. Le sous-ensemble effectif est déterminé par la configuration
 * de chaque agence (Super Admin → Agences → onglet Diffusion), retournée par
 * /api/diffusion/overview `supports`.
 */

export const DIFFUSION_SUPPORTS = [
  { id: 'seloger',           label: 'SeLoger Bureaux & Commerces', icon: 'bi-house-heart-fill',   accent: 'text-red-600',    ring: 'ring-red-100' },
  { id: 'leboncoin',         label: 'Leboncoin Pro',               icon: 'bi-tag-fill',           accent: 'text-orange-600', ring: 'ring-orange-100' },
  { id: 'cessionpme',        label: 'CessionPME',                  icon: 'bi-briefcase-fill',    accent: 'text-indigo-600', ring: 'ring-indigo-100' },
  { id: 'site_vitrine',      label: 'Site vitrine',                icon: 'bi-globe',              accent: 'text-emerald-600',ring: 'ring-emerald-100' },
  // ⛔ RETIRÉS DE L'INTERFACE le 09/09 (lot AP) : logicimmo, bienici, placedescommerces,
  // transentreprise, bureauxlocaux. Leurs connecteurs sont toujours en place côté serveur
  // (services/diffusion_service.py::_PUSHERS) mais ce sont des STUBS : ils écrivaient
  // « Diffusé » et une URL fictive sans qu'aucune annonce ne soit envoyée. Un interrupteur
  // qui ne fait rien est un piège : on ne les propose plus tant qu'ils ne sont pas branchés.
  // Rebranchables à la demande : il suffit de remettre la ligne correspondante ici.
];

export const DIFFUSION_STATUS = {
  DIFFUSE:      'diffuse',
  EN_ATTENTE:   'en_attente',
  ERREUR:       'erreur',
  NON_DIFFUSE:  'non_diffuse',
};

// Configuration visuelle sémantique — respecte le design system (vert/ambre/rouge/gris)
export const DIFFUSION_STATUS_CONFIG = {
  diffuse:      { label: 'Diffusé',     dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  en_attente:   { label: 'En attente',  dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200' },
  erreur:       { label: 'Erreur',      dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50 border-red-200' },
  non_diffuse:  { label: 'Non diffusé', dot: 'bg-slate-300',   text: 'text-slate-500',   bg: 'bg-slate-50 border-slate-200' },
};

/**
 * Normalise l'objet diffusion renvoyé par le backend (ou absent) en garantissant
 * les 4 supports + un shape complet { status, date, url, message }.
 */
export const normalizeDiffusion = (diffusion) => {
  const src = diffusion && typeof diffusion === 'object' ? diffusion : {};
  const result = {};
  DIFFUSION_SUPPORTS.forEach(({ id }) => {
    result[id] = {
      status: src[id]?.status || DIFFUSION_STATUS.NON_DIFFUSE,
      date: src[id]?.date || null,
      url: src[id]?.url || null,
      message: src[id]?.message || null,
      // lot AC3 — identifiant de l'annonce chez le portail : conservé côté écran pour savoir
      // qu'un dépôt existe déjà (sinon un second envoi créerait un doublon).
      classified_id: src[id]?.classified_id || null,
      // lot AD2 — date de DÉPÔT, jamais réécrite par « Rafraîchir le statut » : c'est elle
      // qui permet de dire depuis combien de temps le portail ne répond pas.
      date_depot: src[id]?.date_depot || null,
      // lot AU15 — deux attentes portent le même statut `en_attente` : un DÉPÔT qui attend la
      // mise en ligne, et un RETRAIT déjà parti chez le portail. Sans ce drapeau, l'écran
      // proposait « Annuler l'envoi » sur un retrait — un DELETE de plus, refusé par le portail.
      attente_retrait: src[id]?.attente_retrait || false,
      // lot BG — « en ligne SANS ses photos » est classé `erreur`, mais le portail DÉTIENT
      // l'annonce : c'est le seul cas d'erreur où « Mettre à jour l'annonce » a un sens.
      // ⚠️ Sans cette ligne, le drapeau était perdu ICI, jamais dans la réponse du serveur.
      en_ligne_avec_reserve: src[id]?.en_ligne_avec_reserve === true,
    };
  });
  return result;
};

export const getSupportById = (id) => DIFFUSION_SUPPORTS.find(s => s.id === id);

/**
 * Filtre le catalogue global des supports selon la config renvoyée par
 * /api/diffusion/overview `supports` (liste d'IDs activés pour l'agence).
 * Utilisé par la modale unifiée et la page globale pour n'afficher que
 * les colonnes/toggles pertinents. Préserve l'ordre du catalogue.
 */
export const filterEnabledSupports = (enabledIds) => {
  if (!Array.isArray(enabledIds)) return DIFFUSION_SUPPORTS;
  return DIFFUSION_SUPPORTS.filter(s => enabledIds.includes(s.id));
};

/**
 * Lot BP — UNE seule table de libellés d'état, pour la page Diffusion ET la fenêtre de la
 * fiche bien. Deux écrans qui décrivent le même état avec des mots différents, c'est deux
 * vérités : « En attente » ne disait pas si on attendait une publication ou un RETRAIT, et
 * l'écran proposait « Annuler l'envoi » sur un bien qu'on venait de retirer.
 */
export const MENTION_MODIFIE = 'Modifié — non renvoyé';

export const TON_ETAT = {
  ok:      { dot: 'bg-emerald-500', text: 'text-emerald-700' },
  attente: { dot: 'bg-amber-500',   text: 'text-amber-700' },
  erreur:  { dot: 'bg-red-500',     text: 'text-red-700' },
  muet:    { dot: 'bg-slate-300',   text: 'text-slate-500' },
};

export const etatDiffusionLisible = (etat, modifie = false) => {
  const st = etat?.status || 'non_diffuse';
  // lot AU15 / BP — on attend un RETRAIT, pas une mise en ligne.
  const attenteRetrait = st === 'en_attente' && !!etat?.attente_retrait;
  // lot BG / BN — l'annonce est en ligne, mais le portail a refusé la nouvelle version.
  const enLigneAvecReserve = st === 'erreur' && etat?.en_ligne_avec_reserve === true;

  let label = null;
  let ton = 'muet';
  if (st === 'diffuse') { label = 'En ligne'; ton = 'ok'; }
  else if (attenteRetrait) { label = 'Retrait en cours'; ton = 'attente'; }
  else if (st === 'en_attente') { label = 'Publication en cours'; ton = 'attente'; }
  else if (enLigneAvecReserve) { label = 'En ligne — mise à jour refusée'; ton = 'ok'; }
  else if (st === 'erreur') { label = 'Refusé'; ton = 'erreur'; }

  // Au plus UNE action principale par ligne.
  let action = null;
  if ((st === 'diffuse' && modifie) || enLigneAvecReserve) action = 'update';
  else if (st === 'en_attente' && !attenteRetrait) action = 'cancel';

  return { status: st, label, ton, action, attenteRetrait, enLigneAvecReserve,
           messageRouge: ton === 'erreur' };
};
